import React, { useState } from 'react'
import { lumi } from '../lib/lumi'
import {CheckCircle, Loader2, Send} from 'lucide-react'

interface B2BLeadFormProps {
  currentLang?: 'zh' | 'en'
  onSuccess?: () => void
}

const clientTypeOptions = {
  zh: [
    { value: 'cafe', label: '咖啡廳／獨立選物店' },
    { value: 'brunch', label: '早午餐店／餐酒館' },
    { value: 'office', label: '企業辦公室咖啡' },
    { value: 'hotel', label: '飯店旅宿' },
    { value: 'oem', label: '濾掛 OEM（自有品牌）' },
    { value: 'other', label: '其他' },
  ],
  en: [
    { value: 'cafe', label: 'Cafe / Specialty Store' },
    { value: 'brunch', label: 'Brunch / Wine Bar' },
    { value: 'office', label: 'Corporate Office Coffee' },
    { value: 'hotel', label: 'Hotel / Boutique Stay' },
    { value: 'oem', label: 'Drip Bag OEM (Private Label)' },
    { value: 'other', label: 'Other' },
  ],
}

const monthlyUsageOptions = {
  zh: [
    { value: 'under5kg', label: '5kg 以下（試用）' },
    { value: '5to20kg', label: '5–20kg（小型店面）' },
    { value: 'over20kg', label: '20kg 以上（多據點）' },
  ],
  en: [
    { value: 'under5kg', label: 'Under 5kg (Trial)' },
    { value: '5to20kg', label: '5–20kg (Small Store)' },
    { value: 'over20kg', label: '20kg+ (Multi-location)' },
  ],
}

const B2BLeadForm: React.FC<B2BLeadFormProps> = ({ currentLang = 'zh', onSuccess }) => {
  const lang = currentLang
  const [form, setForm] = useState({
    contactName: '',
    companyName: '',
    phone: '',
    email: '',
    clientType: '',
    monthlyUsage: '',
    message: '',
  })
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const isZh = lang === 'zh'

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.contactName || !form.phone || !form.clientType || !form.monthlyUsage) {
      setError(isZh ? '請填寫所有必填欄位' : 'Please fill in all required fields')
      return
    }

    setLoading(true)
    try {
      await lumi.entities.b2b_leads.create({
        contactName: form.contactName,
        companyName: form.companyName || '',
        phone: form.phone,
        email: form.email || '',
        clientType: form.clientType,
        monthlyUsage: form.monthlyUsage,
        message: form.message || '',
        status: 'new',
        source: 'wholesale',
        creator: 'visitor',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })

      // 寄送通知 Email 給業主
      try {
        await lumi.tools.email.send({
          to: [{ email: 'ongu.studio@gmail.com', name: 'ONGU Studio' }],
          subject: `[B2B 詢問] ${form.companyName || form.contactName} — ${clientTypeOptions.zh.find(o => o.value === form.clientType)?.label || form.clientType}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 24px; background: #f9f9f9;">
              <h2 style="color: #1a1a1a; font-weight: 300; letter-spacing: 2px; border-bottom: 1px solid #ddd; padding-bottom: 12px;">
                🧾 新 B2B 試豆申請
              </h2>
              <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
                <tr>
                  <td style="padding: 10px 0; color: #666; width: 130px; font-size: 13px;">聯絡人</td>
                  <td style="padding: 10px 0; color: #1a1a1a; font-weight: 500;">${form.contactName}</td>
                </tr>
                ${form.companyName ? `<tr>
                  <td style="padding: 10px 0; color: #666; font-size: 13px;">公司/店名</td>
                  <td style="padding: 10px 0; color: #1a1a1a;">${form.companyName}</td>
                </tr>` : ''}
                <tr>
                  <td style="padding: 10px 0; color: #666; font-size: 13px;">電話/LINE</td>
                  <td style="padding: 10px 0; color: #1a1a1a; font-weight: 500;">${form.phone}</td>
                </tr>
                ${form.email ? `<tr>
                  <td style="padding: 10px 0; color: #666; font-size: 13px;">Email</td>
                  <td style="padding: 10px 0; color: #1a1a1a;">${form.email}</td>
                </tr>` : ''}
                <tr>
                  <td style="padding: 10px 0; color: #666; font-size: 13px;">客群類型</td>
                  <td style="padding: 10px 0; color: #1a1a1a;">${clientTypeOptions.zh.find(o => o.value === form.clientType)?.label || form.clientType}</td>
                </tr>
                <tr>
                  <td style="padding: 10px 0; color: #666; font-size: 13px;">每月用量</td>
                  <td style="padding: 10px 0; color: #1a1a1a;">${monthlyUsageOptions.zh.find(o => o.value === form.monthlyUsage)?.label || form.monthlyUsage}</td>
                </tr>
                ${form.message ? `<tr>
                  <td style="padding: 10px 0; color: #666; font-size: 13px; vertical-align: top;">補充說明</td>
                  <td style="padding: 10px 0; color: #1a1a1a;">${form.message}</td>
                </tr>` : ''}
              </table>
              <div style="margin-top: 24px; padding: 12px 16px; background: #1a1a1a; color: #ccc; font-size: 12px; letter-spacing: 1px;">
                申請時間：${new Date().toLocaleString('zh-TW')} ｜ 來源：wholesale 頁面
              </div>
              <p style="margin-top: 16px; font-size: 12px; color: #999;">請於 24 小時內聯絡客戶，完成後至後台更新進度。</p>
            </div>
          `,
        })
      } catch (emailErr) {
        // 郵件失敗不影響主流程
        console.error('Email notification failed:', emailErr)
      }

      setSubmitted(true)
      onSuccess?.()
    } catch (err) {
      console.error('B2B lead submission failed:', err)
      setError(isZh ? '提交失敗，請稍後再試或直接聯繫我們' : 'Submission failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center mb-6">
          <CheckCircle className="w-14 h-14 text-zinc-900 dark:text-zinc-100" strokeWidth={1} />
        </div>
        <h3 className="text-2xl font-extralight tracking-wider text-zinc-900 dark:text-zinc-100 mb-4">
          {isZh ? '申請已送出' : 'Application Submitted'}
        </h3>
        <div className="w-12 h-[1px] bg-zinc-400 mx-auto mb-6"></div>
        <p className="text-sm font-light text-zinc-600 dark:text-zinc-400 leading-loose max-w-sm">
          {isZh
            ? '感謝您的試豆申請，我們將於 24 小時內與您聯繫，確認樣本寄送細節。'
            : 'Thank you for your sample request. We will contact you within 24 hours to arrange shipment details.'}
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Row 1: 聯絡人 + 公司名 */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-light tracking-[0.2em] text-zinc-500 dark:text-zinc-400 uppercase mb-2">
            {isZh ? '聯絡人姓名' : 'Contact Name'} <span className="text-zinc-400">*</span>
          </label>
          <input
            type="text"
            name="contactName"
            value={form.contactName}
            onChange={handleChange}
            placeholder={isZh ? '您的姓名' : 'Your name'}
            className="w-full px-4 py-3 bg-transparent border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 text-sm font-light focus:outline-none focus:border-zinc-500 dark:focus:border-zinc-400 placeholder-zinc-400 dark:placeholder-zinc-600 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-light tracking-[0.2em] text-zinc-500 dark:text-zinc-400 uppercase mb-2">
            {isZh ? '公司 / 店名' : 'Company / Store'}
          </label>
          <input
            type="text"
            name="companyName"
            value={form.companyName}
            onChange={handleChange}
            placeholder={isZh ? '選填' : 'Optional'}
            className="w-full px-4 py-3 bg-transparent border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 text-sm font-light focus:outline-none focus:border-zinc-500 dark:focus:border-zinc-400 placeholder-zinc-400 dark:placeholder-zinc-600 transition-colors"
          />
        </div>
      </div>

      {/* Row 2: 電話 + Email */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-light tracking-[0.2em] text-zinc-500 dark:text-zinc-400 uppercase mb-2">
            {isZh ? '電話 / LINE' : 'Phone / LINE'} <span className="text-zinc-400">*</span>
          </label>
          <input
            type="text"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder={isZh ? '0912-345-678' : '0912-345-678'}
            className="w-full px-4 py-3 bg-transparent border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 text-sm font-light focus:outline-none focus:border-zinc-500 dark:focus:border-zinc-400 placeholder-zinc-400 dark:placeholder-zinc-600 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-light tracking-[0.2em] text-zinc-500 dark:text-zinc-400 uppercase mb-2">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder={isZh ? '選填' : 'Optional'}
            className="w-full px-4 py-3 bg-transparent border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 text-sm font-light focus:outline-none focus:border-zinc-500 dark:focus:border-zinc-400 placeholder-zinc-400 dark:placeholder-zinc-600 transition-colors"
          />
        </div>
      </div>

      {/* Row 3: 客群類型 + 月用量 */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-light tracking-[0.2em] text-zinc-500 dark:text-zinc-400 uppercase mb-2">
            {isZh ? '業態類型' : 'Business Type'} <span className="text-zinc-400">*</span>
          </label>
          <select
            name="clientType"
            value={form.clientType}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 text-sm font-light focus:outline-none focus:border-zinc-500 dark:focus:border-zinc-400 transition-colors"
          >
            <option value="">{isZh ? '請選擇' : 'Select type'}</option>
            {clientTypeOptions[lang].map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-light tracking-[0.2em] text-zinc-500 dark:text-zinc-400 uppercase mb-2">
            {isZh ? '預估每月用量' : 'Monthly Usage'} <span className="text-zinc-400">*</span>
          </label>
          <select
            name="monthlyUsage"
            value={form.monthlyUsage}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 text-sm font-light focus:outline-none focus:border-zinc-500 dark:focus:border-zinc-400 transition-colors"
          >
            <option value="">{isZh ? '請選擇' : 'Select usage'}</option>
            {monthlyUsageOptions[lang].map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 補充說明 */}
      <div>
        <label className="block text-xs font-light tracking-[0.2em] text-zinc-500 dark:text-zinc-400 uppercase mb-2">
          {isZh ? '補充說明（選填）' : 'Additional Notes (Optional)'}
        </label>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          rows={3}
          placeholder={isZh
            ? '例如：需要試豆的風味方向、目前使用的機器、特別需求等'
            : 'e.g., flavor preference, equipment used, special requirements'}
          className="w-full px-4 py-3 bg-transparent border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 text-sm font-light focus:outline-none focus:border-zinc-500 dark:focus:border-zinc-400 placeholder-zinc-400 dark:placeholder-zinc-600 transition-colors resize-none"
        />
      </div>

      {error && (
        <p className="text-sm text-red-500 dark:text-red-400 font-light tracking-wide">{error}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 py-4 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-sm font-light tracking-[0.2em] uppercase hover:bg-zinc-700 dark:hover:bg-zinc-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Send className="w-4 h-4" />
        )}
        {loading
          ? (isZh ? '送出中...' : 'Submitting...')
          : (isZh ? '申請免費試豆樣本' : 'Request Free Sample')}
      </button>

      <p className="text-xs text-center font-light text-zinc-400 dark:text-zinc-500 tracking-wide">
        {isZh
          ? '我們通常於 24 小時內回覆，並安排樣本寄送。'
          : 'We typically respond within 24 hours and arrange sample shipping.'}
      </p>
    </form>
  )
}

export default B2BLeadForm
